<script setup lang="ts">
/**
 * AiView — 1:1 порт docs/mockups/public-ai.html (inbox-163).
 *
 * Спека: docs/specs/2026-05-19-public-behavior.md §29 «AI-копайлот».
 * STYLE_NOTES: hero 88px / 56px title / gradient text / hero-badge.
 *
 * Секции:
 *  1. Hero с badge «На базе YandexGPT · Данные не покидают РФ» + CTA
 *  2. Демо-чат UI (внутри hero после CTA) — 4 bubble'а user/bot
 *  3. Use cases — 6 cards в grid 2-col (Прогноз CTR / Smart Time / Аномалии /
 *     Auto-UTM / Smart Preview / Авто-отчёты)
 *  4. YandexGPT banner («Я» лого + 2-абзаца pitch)
 *  5. Privacy — 3 cards (152-ФЗ / Не уходит за пределы РФ / Не обучаемся)
 *  6. Final CTA «Попробуйте копайлот бесплатно»
 *
 * AppPublic уже даёт sticky nav + footer, поэтому здесь только content.
 */

import AppPublic from '@/layouts/AppPublic.vue';
import { usePageHead, productJsonLd } from '@/composables/usePageHead';

usePageHead({
  title: 'AI-копайлот для аналитики ссылок',
  description:
    'Спросите «почему CR упала» — AI-копайлот проанализирует ваши клики, события и конверсии и выдаст insights на естественном языке. Powered by YandexGPT.',
  path: '/ai',
  type: 'website',
  jsonLd: productJsonLd(
    'AI-копайлот — link-platform',
    'Аналитика ссылок естественным языком на базе YandexGPT, данные не покидают РФ.',
  ),
});

interface UseCase {
  icon: 'chart' | 'clock' | 'shield' | 'code' | 'image' | 'doc';
  title: string;
  desc: string;
  prompt: string;
}
const USE_CASES: UseCase[] = [
  {
    icon: 'chart',
    title: 'Прогноз CTR',
    desc: 'AI обучен на ваших исторических данных и предсказывает CTR новой кампании ещё до запуска. Точность ~85% на горизонте 7 дней.',
    prompt: '«Какой CTR будет у новой кампании в TG на аудиторию 25-35?»',
  },
  {
    icon: 'clock',
    title: 'Smart Time',
    desc: 'Узнайте лучшее время публикации для конкретного канала и аудитории. Учитывается ваша история, дни недели, праздники, конкуренты.',
    prompt: '«Когда лучше запустить рекламу новой коллекции в Instagram?»',
  },
  {
    icon: 'shield',
    title: 'Детект аномалий',
    desc: 'Аномальные спайки трафика, бот-фермы, накрутки — AI замечает их в первые 15 минут и присылает алерт в TG или email.',
    prompt: '«Что необычного произошло сегодня в моих кампаниях?»',
  },
  {
    icon: 'code',
    title: 'Auto-UTM',
    desc: 'Опишите кампанию на русском — AI сгенерирует структурированные UTM-параметры, согласованные с вашим naming convention.',
    prompt: '«Сделай UTM для рекламы в VK на ЦА 30-45 со скидкой 20%»',
  },
  {
    icon: 'image',
    title: 'Smart Preview',
    desc: 'AI сгенерирует превью-картинку и заголовок для VK / Telegram / OG — оптимизированные под клик. Можно править перед публикацией.',
    prompt: '«Сделай OG-картинку для распродажи летней коллекции»',
  },
  {
    icon: 'doc',
    title: 'Авто-отчёты',
    desc: 'Раз в неделю AI собирает краткий отчёт по кампаниям с инсайтами и рекомендациями. Можно отправить клиенту одним кликом.',
    prompt: '«Подготовь отчёт за апрель для агентства Альфа»',
  },
];
</script>

<template>
  <AppPublic>
    <!-- HERO + Chat demo -->
    <section class="hero">
      <div class="container">
        <div class="hero-content">
          <div class="hero-badge">На базе YandexGPT · Данные не покидают РФ</div>
          <h1 class="hero-title">
            Маркетолог спрашивает —<br>
            <span class="gradient">AI отвечает</span>
          </h1>
          <p class="hero-sub">
            Естественный язык вместо SQL и Excel. Задавайте вопросы по своим
            кампаниям обычной речью — копайлот достанет данные, найдёт аномалии,
            предложит действия.
          </p>
          <div class="hero-cta">
            <a
              href="/signup"
              class="btn btn-primary"
            >Попробовать бесплатно</a>
            <a
              href="#demo"
              class="btn btn-outline"
            >Посмотреть демо</a>
          </div>
        </div>

        <!-- Chat demo -->
        <div
          id="demo"
          class="chat-demo-wrap"
        >
          <div class="chat-demo">
            <header class="chat-head">
              <div class="chat-avatar">AI</div>
              <div>
                <div class="name">Копайлот link-platform</div>
                <div class="role">YandexGPT · Контекст: 30 дней, 2 847 ссылок, 14 кампаний</div>
              </div>
              <div class="status">Активен</div>
            </header>
            <div class="chat-body">
              <div class="msg user">
                Почему кампания «Лето-распродажа» стала хуже на прошлой неделе?
              </div>
              <div class="msg bot">
                За неделю CR упал с <strong>5.2%</strong> до <strong>3.1%</strong>. Причина —
                рост фрод-трафика с источника <strong>vk_target_lal</strong>: было 4%,
                стало 28%.
                <div class="data-block">
                  <b>Топ-3 источника по фрод-доле:</b><br>
                  · vk_target_lal — 28% (▲ +24пп)<br>
                  · tg_ads_interest — 12% (▲ +3пп)<br>
                  · yandex_direct_search — 1.2% (стабильно)
                </div>
                Рекомендую отключить <strong>vk_target_lal</strong> и перераспределить
                бюджет на <strong>yandex_direct_search</strong>, где CR держится ~5.8%. Применить?
              </div>
              <div class="msg user">
                Сколько примерно потеряем выручки если не действовать неделю?
              </div>
              <div class="msg bot">
                При текущей динамике — потеря ≈ <strong>340 000 ₽</strong> за неделю
                (в среднем 48 600 ₽/день недополучения относительно baseline предыдущего месяца).
                <div class="data-block">
                  <b>Допущения:</b><br>
                  · Средний чек: 4 200 ₽<br>
                  · Конверсии до фрод-спайка: 11.8/день<br>
                  · Сейчас фактически: 6.6/день
                </div>
                Хотите — настрою автоматический алерт на фрод-долю выше 15%?
              </div>
            </div>
            <div class="chat-input-row">
              <input
                type="text"
                placeholder="Спросите что-нибудь о ваших кампаниях…"
                aria-label="Demo input"
                disabled
              >
              <button
                type="button"
                class="send-btn"
                aria-label="Отправить"
                disabled
              >→</button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- USE CASES -->
    <section class="usecases-section">
      <div class="container">
        <div class="section-head">
          <span class="section-eyebrow">5 сценариев</span>
          <h2>Где копайлот экономит часы работы</h2>
          <p class="section-sub">
            Вместо ручного SQL и Excel — задавайте вопросы голосом или текстом.
            Все ответы основаны на ваших реальных данных за последние 90 дней.
          </p>
        </div>
        <div class="usecase-grid">
          <article
            v-for="u in USE_CASES"
            :key="u.title"
            class="usecase"
          >
            <div class="usecase-icon">
              <svg
                v-if="u.icon === 'chart'"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M3 3v18h18" />
                <path d="m7 14 4-4 4 4 5-5" />
              </svg>
              <svg
                v-else-if="u.icon === 'clock'"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <svg
                v-else-if="u.icon === 'shield'"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <svg
                v-else-if="u.icon === 'code'"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="16 18 22 12 16 6" />
                <polyline points="8 6 2 12 8 18" />
              </svg>
              <svg
                v-else-if="u.icon === 'image'"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18M9 21V9" />
              </svg>
              <svg
                v-else
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </div>
            <h3>{{ u.title }}</h3>
            <p>{{ u.desc }}</p>
            <div class="prompt-example">{{ u.prompt }}</div>
          </article>
        </div>
      </div>
    </section>

    <!-- YandexGPT banner -->
    <section>
      <div class="container">
        <div class="ygpt-banner">
          <div class="ygpt-logo">Я</div>
          <div>
            <h3>На базе YandexGPT — данные не покидают Россию</h3>
            <p>
              В отличие от ChatGPT, OpenAI или Anthropic, наш AI работает через
              YandexGPT API с серверами в России. Ваши кампании, аналитика и переписки
              никогда не передаются в зарубежные облака.
            </p>
            <p>
              Это критично для импортозамещающих проектов и компаний под санкционными
              ограничениями. Аккредитация Минцифры на текущем рассмотрении.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- PRIVACY -->
    <section>
      <div class="container">
        <div class="section-head">
          <span class="section-eyebrow">Privacy by design</span>
          <h2>Где живут ваши данные</h2>
          <p class="section-sub">
            Прозрачные правила обработки персональных и коммерческих данных.
            Никаких «улучшений моделей на ваших промптах».
          </p>
        </div>
        <div class="privacy-grid">
          <article class="privacy-card">
            <div class="ic">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h4>152-ФЗ compliance</h4>
            <p>
              Все ПДн хранятся на серверах Yandex Cloud в Москве и Питере. Уведомлены
              в Роскомнадзоре как оператор.
            </p>
          </article>
          <article class="privacy-card">
            <div class="ic">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20" />
                <path d="M12 2a14.5 14.5 0 0 0 0 20" />
              </svg>
            </div>
            <h4>Не уходит за пределы РФ</h4>
            <p>
              Ваши промпты и данные не отправляются OpenAI, Anthropic или Google —
              только YandexGPT в российском контуре.
            </p>
          </article>
          <article class="privacy-card">
            <div class="ic">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <h4>Не обучаемся на ваших данных</h4>
            <p>
              Промпты используются только для генерации ответа. Не сохраняются для
              дообучения LLM. Логи удаляются через 30 дней.
            </p>
          </article>
        </div>
      </div>
    </section>

    <!-- FINAL CTA -->
    <section class="final-cta-wrap">
      <div class="container">
        <h2>Попробуйте копайлот бесплатно</h2>
        <p>50 запросов в месяц на тарифе Старт. Без лимитов на Бизнес.</p>
        <div class="hero-cta hero-cta-large">
          <a
            href="/signup"
            class="btn btn-primary"
          >Создать аккаунт</a>
          <a
            href="/pricing"
            class="btn btn-outline"
          >Посмотреть тарифы</a>
        </div>
      </div>
    </section>
  </AppPublic>
</template>

<style scoped>
.container {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 32px;
}

/* HERO */
.hero {
  position: relative;
  padding: 88px 0 64px;
  overflow: hidden;
}
.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 60% 50% at 30% 20%, rgba(79, 140, 255, 0.15), transparent 70%),
    radial-gradient(ellipse 50% 40% at 80% 30%, rgba(124, 92, 255, 0.10), transparent 70%);
  pointer-events: none;
}
.hero-content {
  position: relative;
  max-width: 760px;
  margin: 0 auto;
  text-align: center;
}
.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  margin-bottom: 24px;
  background: rgba(79, 140, 255, 0.12);
  border: 1px solid rgba(79, 140, 255, 0.25);
  border-radius: 100px;
  font-size: 13px;
  color: #6ea1ff;
  font-weight: 500;
}
.hero-badge::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4f8cff;
  box-shadow: 0 0 8px #4f8cff;
}
h1.hero-title {
  font-size: 56px;
  line-height: 1.08;
  letter-spacing: -0.025em;
  font-weight: 700;
  margin-bottom: 24px;
  color: #e8edf3;
}
h1.hero-title .gradient {
  background: linear-gradient(135deg, #4f8cff 0%, #7c5cff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.hero-sub {
  font-size: 19px;
  line-height: 1.55;
  color: #8b95a5;
  max-width: 640px;
  margin: 0 auto 36px;
}
.hero-cta {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 13px 22px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 15px;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid transparent;
  text-decoration: none;
}
.btn-primary {
  background: #4f8cff;
  color: #fff;
  border-color: #4f8cff;
}
.btn-primary:hover {
  background: #6ea1ff;
  border-color: #6ea1ff;
}
.btn-outline {
  border-color: #2a3441;
  color: #e8edf3;
  background: transparent;
}
.btn-outline:hover {
  border-color: #8b95a5;
  background: #11161e;
}
.hero-cta-large .btn {
  padding: 14px 28px;
  font-size: 16px;
}

/* CHAT DEMO */
.chat-demo-wrap {
  max-width: 820px;
  margin: 64px auto 0;
  position: relative;
}
.chat-demo-wrap::before {
  content: '';
  position: absolute;
  inset: -50px -80px;
  background: linear-gradient(135deg, #4f8cff 0%, #7c5cff 100%);
  opacity: 0.16;
  filter: blur(80px);
  border-radius: 50%;
  z-index: -1;
}
.chat-demo {
  background: #161c26;
  border: 1px solid #2a3441;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5);
}
.chat-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #1f2733;
  background: #11161e;
}
.chat-avatar {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #4f8cff 0%, #7c5cff 100%);
  display: grid;
  place-items: center;
  font-size: 13px;
  font-weight: 800;
  color: #fff;
}
.chat-head .name {
  font-size: 14.5px;
  font-weight: 600;
  color: #e8edf3;
}
.chat-head .role {
  font-size: 12px;
  color: #5a6577;
  margin-top: 2px;
}
.chat-head .status {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #3ecf8e;
}
.chat-head .status::before {
  content: '';
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #3ecf8e;
  box-shadow: 0 0 8px #3ecf8e;
}

.chat-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  text-align: left;
}
.msg {
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 14px;
  font-size: 14.5px;
  line-height: 1.55;
}
.msg.user {
  align-self: flex-end;
  background: #4f8cff;
  color: #fff;
  border-bottom-right-radius: 4px;
}
.msg.bot {
  align-self: flex-start;
  background: #11161e;
  border: 1px solid #1f2733;
  border-bottom-left-radius: 4px;
  color: #e8edf3;
}
.msg.bot strong {
  color: #6ea1ff;
}
.msg.bot .data-block {
  margin-top: 10px;
  padding: 10px 12px;
  background: #161c26;
  border: 1px solid #1f2733;
  border-radius: 8px;
  font-family: 'SF Mono', Menlo, monospace;
  font-size: 12.5px;
  color: #8b95a5;
}
.msg.bot .data-block b {
  color: #e8edf3;
}

.chat-input-row {
  padding: 14px 20px;
  border-top: 1px solid #1f2733;
  display: flex;
  gap: 10px;
  align-items: center;
}
.chat-input-row input {
  flex: 1;
  background: #11161e;
  border: 1px solid #1f2733;
  color: #e8edf3;
  border-radius: 8px;
  padding: 10px 14px;
  font: inherit;
  font-size: 14px;
  outline: 0;
}
.chat-input-row input::placeholder {
  color: #5a6577;
}
.chat-input-row .send-btn {
  width: 38px;
  height: 38px;
  background: #4f8cff;
  border: 0;
  border-radius: 8px;
  color: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  font-size: 18px;
}
.chat-input-row .send-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* SECTIONS */
section {
  padding: 80px 0;
}
.section-head {
  text-align: center;
  margin-bottom: 56px;
}
.section-eyebrow {
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  color: #6ea1ff;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 14px;
}
h2 {
  font-size: 38px;
  line-height: 1.15;
  letter-spacing: -0.02em;
  font-weight: 700;
  margin-bottom: 14px;
  color: #e8edf3;
}
.section-sub {
  font-size: 17px;
  color: #8b95a5;
  max-width: 580px;
  margin: 0 auto;
}

/* USE CASES */
.usecases-section {
  background: #11161e;
  border-top: 1px solid #1f2733;
  border-bottom: 1px solid #1f2733;
}
.usecase-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}
.usecase {
  background: #161c26;
  border: 1px solid #1f2733;
  border-radius: 14px;
  padding: 28px;
  transition: all 0.15s;
}
.usecase:hover {
  border-color: #4f8cff;
  transform: translateY(-1px);
}
.usecase-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(79, 140, 255, 0.12);
  color: #6ea1ff;
  display: grid;
  place-items: center;
  margin-bottom: 18px;
}
.usecase-icon svg {
  width: 22px;
  height: 22px;
}
.usecase h3 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #e8edf3;
}
.usecase p {
  font-size: 14.5px;
  color: #8b95a5;
  line-height: 1.55;
}
.usecase .prompt-example {
  margin-top: 14px;
  padding: 10px 12px;
  background: #11161e;
  border-left: 2px solid #4f8cff;
  border-radius: 0 6px 6px 0;
  font-size: 13px;
  color: #8b95a5;
  font-style: italic;
}

/* YANDEXGPT BANNER */
.ygpt-banner {
  background: #161c26;
  border: 1px solid #2a3441;
  border-radius: 16px;
  padding: 48px 56px;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 36px;
  align-items: center;
  position: relative;
  overflow: hidden;
}
.ygpt-banner::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse 60% 100% at 0% 50%,
    rgba(252, 52, 9, 0.10),
    transparent 70%
  );
  pointer-events: none;
}
.ygpt-banner > * {
  position: relative;
}
.ygpt-logo {
  width: 88px;
  height: 88px;
  border-radius: 18px;
  background: linear-gradient(135deg, #fc3409, #ffba00);
  display: grid;
  place-items: center;
  color: #fff;
  font-size: 38px;
  font-weight: 800;
  box-shadow: 0 12px 30px rgba(252, 52, 9, 0.3);
}
.ygpt-banner h3 {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-bottom: 10px;
  color: #e8edf3;
}
.ygpt-banner p {
  font-size: 15.5px;
  color: #8b95a5;
  line-height: 1.6;
}
.ygpt-banner p + p {
  margin-top: 8px;
}

/* PRIVACY */
.privacy-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
}
.privacy-card {
  background: #161c26;
  border: 1px solid #1f2733;
  border-radius: 12px;
  padding: 26px;
}
.privacy-card .ic {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(79, 140, 255, 0.12);
  color: #6ea1ff;
  display: grid;
  place-items: center;
  margin-bottom: 14px;
}
.privacy-card .ic svg {
  width: 18px;
  height: 18px;
}
.privacy-card h4 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #e8edf3;
}
.privacy-card p {
  font-size: 14px;
  color: #8b95a5;
  line-height: 1.55;
}

/* FINAL CTA */
.final-cta-wrap {
  padding: 120px 0;
  text-align: center;
  position: relative;
  overflow: hidden;
}
.final-cta-wrap::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse 60% 50% at 50% 100%,
    rgba(79, 140, 255, 0.18),
    transparent 70%
  );
  pointer-events: none;
}
.final-cta-wrap .container {
  position: relative;
}
.final-cta-wrap h2 {
  font-size: 44px;
  margin-bottom: 14px;
}
.final-cta-wrap p {
  font-size: 18px;
  color: #8b95a5;
  margin-bottom: 32px;
}

/* RESPONSIVE */
@media (max-width: 980px) {
  .container {
    padding: 0 24px;
  }
  h1.hero-title {
    font-size: 44px;
  }
  .hero-sub {
    font-size: 17px;
  }
  h2 {
    font-size: 32px;
  }
  .final-cta-wrap h2 {
    font-size: 34px;
  }
  .usecase-grid {
    grid-template-columns: 1fr;
  }
  .ygpt-banner {
    grid-template-columns: 1fr;
    padding: 36px 32px;
    text-align: center;
  }
  .ygpt-logo {
    margin: 0 auto;
  }
  .privacy-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 640px) {
  .container {
    padding: 0 18px;
  }
  .hero {
    padding: 48px 0 40px;
  }
  h1.hero-title {
    font-size: 30px;
  }
  .hero-sub {
    font-size: 15.5px;
    margin-bottom: 24px;
  }
  .hero-cta {
    flex-direction: column;
    gap: 10px;
  }
  .hero-cta .btn {
    width: 100%;
    justify-content: center;
  }
  section {
    padding: 56px 0;
  }
  .section-head {
    margin-bottom: 36px;
  }
  h2 {
    font-size: 26px;
  }
  .chat-body {
    padding: 18px;
    gap: 10px;
  }
  .msg {
    font-size: 14px;
    max-width: 90%;
  }
  .usecase {
    padding: 22px;
  }
  .ygpt-banner {
    padding: 28px 22px;
  }
  .ygpt-logo {
    width: 64px;
    height: 64px;
    font-size: 28px;
  }
  .privacy-card {
    padding: 22px;
  }
  .final-cta-wrap {
    padding: 72px 0;
  }
  .final-cta-wrap h2 {
    font-size: 26px;
  }
  .final-cta-wrap p {
    font-size: 15.5px;
  }
}
</style>
